<template>
  <div class="car-display">
    <div class="text-center">
      <img class="img-thumbnail btn-margin" :src="'https://techcar-dev.s3.amazonaws.com/'+ ( car.CarType.AmazonImage.objectKey ? car.CarType.AmazonImage.objectKey : 'spacer.gif' ) + '?v=' + new Date().getTime()"/>
      <br/>
      <router-link :to="{ path: '/fleet/vehicles/' + car.uuid }">
        {{ car.name }} (#{{ car.convadisId }} - {{ car.rego }})
      </router-link>
      <br/>
      <p><strong>{{ car.CarType.name }}</strong></p>
    </div>
    <accordian-display
      :title="'General'"
      :icon="'fa-car'"
      :defaultOpen="true">
      <tr>
        <td class="font-weight-bold">Model</td>
        <td>
          {{ car.CarType.name }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Year of Manufacture</td>
        <td>
          {{ car.CarType.year }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Colour</td>
        <td>
          {{ car.color }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Features</td>
        <td>
          <div v-for="feature in car.CarFeatures" :key="feature.uuid">
            <span class="fa-lg fa-fw"
            :class="[
              feature.icon,
              {
                'fab': feature.icon === 'fa-bluetooth',
                'fa': feature.icon !== 'fa-bluetooth'
              }
            ]" :title="feature.name"></span>
          </div>
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Fuel Card</td>
        <td>
          <span v-if="car.PetrolCards && car.PetrolCards.length">
            <label class="label label-success">
              <i class="fa fa-credit-card"></i> {{ car.PetrolCards[0].name }} - {{ car.PetrolCards[0].number }}
            </label>
            <button class="btn btn-xs btn-danger" @click="cancelCard(car.PetrolCards[0])">
              <i class="fa fa-ban"></i> Cancel
            </button>
          </span>
          <span v-else>
            <label class="label label-warning">No fuel card.</label>
            <button class="btn btn-xs btn-primary" v-if="!car.PetrolCards || !car.PetrolCards.length" @click="newPetrolCard(car)">
              <i class="fa fa-plus"></i> Create New
            </button>
          </span>
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Odometer</td>
        <td>
          {{ car.odometer }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">VIN Number</td>
        <td>
          {{ car.vinNumber }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">One Way Trips</td>
        <td>
          <span class="fa" :class="car.oneWayTrips ? 'fa-check text-success' : 'fa-times text-danger'"></span>
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Monthly Contract Cost</td>
        <td>
          {{ car.monthlyContractCost | to_dollar_amount }}
        </td>
      </tr>
    </accordian-display>
    <accordian-display
      :icon="'fa-map-marker-alt'"
      :title="'Lot Details'">
      <template v-if="lotInformation">
        <tr>
          <td class="font-weight-bold">Address</td>
          <td>
            {{ lotInformation.address }}
          </td>
        </tr>
        <tr>
          <td class="font-weight-bold">Memo</td>
          <td>
            {{ lotInformation.addressMemo }}
          </td>
        </tr>
        <tr>
          <td class="font-weight-bold">Bays</td>
          <td>
            {{ lotInformation.bays }}
          </td>
        </tr>
        <tr>
          <td class="font-weight-bold">Lot Premium</td>
          <td>
            <span v-if="parseInt(lotInformation.premiumAmount)">
              {{ lotInformation.premiumName }} - {{ lotInformation.premiumAmount | to_dollar_amount }}
            </span>
            <em v-else>No premium on this lot.</em>
          </td>
        </tr>
        <tr>
          <td class="font-weight-bold">Weekend Rate</td>
          <td>
            <span class="fa" :class="lotInformation.hasWeekend ? 'fa-check text-success' : 'fa-times text-danger'"></span>
          </td>
        </tr>
        <tr>
          <td class="font-weight-bold">Overnight Rate</td>
          <td>
            <span class="fa" :class="lotInformation.hasOvernight ? 'fa-check text-success' : 'fa-times text-danger'"></span>
          </td>
        </tr>
        <tr>
          <td colspan="2">
            <img :src="lotLocation" alt="Lot Location" class="img-fluid">
          </td>
        </tr>
      </template>
      <tr v-else>
        <td colspan="2">
          <em>Car not on a lot.</em>
        </td>
      </tr>
    </accordian-display>
    <accordian-display
      :icon="'fa-wrench'"
      :title="'Service Details'">
      <tr>
        <td class="font-weight-bold">Last Service Date</td>
        <td>
          {{ car.lastServiceDate | short_date_format }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Next Service Date</td>
        <td>
          {{ car.nextServiceDate | short_date_format }}
        </td>
      </tr>
      <template v-if="car.ServicePerson">
        <tr>
          <td class="font-weight-bold">Service Person</td>
          <td>
            <router-link :to="{ path: '/membership/members/' + car.ServicePerson.uuid }">
              {{ car.ServicePerson.fullName }}
            </router-link>
          </td>
        </tr>
        <tr>
          <td class="font-weight-bold">Service Person Contact Numbers</td>
          <td>
            H: <a :href="`tel:${car.ServicePerson.homePhone}`">
              {{ car.ServicePerson.homePhone }}
            </a><br/>
            W: <a :href="`tel:${car.ServicePerson.workPhone}`">
              {{ car.ServicePerson.workPhone }}
            </a><br/>
            M: <a :href="`tel:${car.ServicePerson.mobilePhone}`">
              {{ car.ServicePerson.mobilePhone }}
            </a>
          </td>
        </tr>
      </template>
      <template v-else>
        <tr>
          <td class="font-weight-bold">Service Person</td>
          <td>
            <em>No service person set</em>
          </td>
        </tr>
      </template>
    </accordian-display>
    <accordian-display
      :icon="'fa-ambulance'"
      :title="'24/7 Roadside Assist Details'">
      <tr>
        <td class="font-weight-bold">Provider</td>
        <td>
          {{ car.roadAssistProvider }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Provider Phone</td>
        <td>
          <a :href="`tel:${car.roadAssistPhone}`">
            {{ car.roadAssistPhone }}
          </a>
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Expiry</td>
        <td>
          {{ car.roadAssistExpiry | short_date_format }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Notes</td>
        <td>
          {{ car.roadAssistNotes }}
        </td>
      </tr>
    </accordian-display>
    <accordian-display
      :title="'Hardware'"
      :icon="'fa-microchip'">
      <template v-if="car.CarHardware">
        <tr>
          <td class="font-weight-bold">Provider</td>
          <td>
            {{ car.CarHardware.provider }}
          </td>
        </tr>
        <tr>
          <td class="font-weight-bold">Serial Number</td>
          <td>
            {{ car.CarHardware.serial }}
          </td>
        </tr>
        <tr>
          <td class="font-weight-bold">Firmware Version</td>
          <td>
            {{ car.CarHardware.firmwareVersion }}
          </td>
        </tr>
        <tr>
          <td class="font-weight-bold">No Problems</td>
          <td>
            <span class="fa" :class="!car.CarHardware.problems ? 'fa-check text-success' : 'fa-times text-danger'"></span>
          </td>
        </tr>
        <tr>
          <td class="font-weight-bold">Notes</td>
          <td>
            {{ car.CarHardware.notes }}
          </td>
        </tr>
      </template>
      <!-- Edit this when convadis is done -->
      <tr v-else>
        <td colspan="2">
          <em>Convadis</em>
        </td>
      </tr>
    </accordian-display>
    <accordian-display
      :icon="'fa-user'"
      :title="'Valets'">
      <tr v-if="!valets.length">
        <td colspan="2">
          <em>No valets.</em>
        </td>
      </tr>
      <template v-for="(valet, i) in valets">
        <tr :key="i">
          <td class="font-weight-bold">#{{ valet.reservationId }}</td>
          <td>
            <router-link :to="'/membership/members/' + valet.Account.uuid">
              {{ valet.Account.dropdownLabel }}
            </router-link>
            <span class="badge badge-success" v-if="valet.Account.Smartcard && valet.Account.Smartcard.uuid">
              <i class="fa fa-id-card"></i> {{ valet.Account.Smartcard.cardNumber }}
            </span>
            <span class="badge badge-danger" v-else>
              <i class="fa fa-id-card"></i> No Smartcard Assigned
            </span>
            <span v-if="valet.count > 1">({{ valet.count }} times)</span>
          </td>
        </tr>
      </template>
    </accordian-display>
    <accordian-display
      :icon="'fa-certificate'"
      :title="'Registration Details'">
      <tr>
        <td class="font-weight-bold">State Registered</td>
        <td>
          {{ car.stateRegistered }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Registered Date</td>
        <td>
          {{ car.carRegisteredDate | short_date_format }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Expiry Date</td>
        <td>
          {{ car.carExpiryDate | short_date_format }}
        </td>
      </tr>
    </accordian-display>
    <accordian-display
      :icon="'fa-dollar-sign'"
      :title="'Purchase Details'">
      <tr>
        <td class="font-weight-bold">Finance Company</td>
        <td>
          {{ car.financeCompany }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Purchase Price</td>
        <td>
          {{ car.purchasePrice | to_dollar_amount }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">End of Term Payout</td>
        <td>
          {{ car.endOfTermPayout | to_dollar_amount }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Lease Period</td>
        <td>
          {{ car.leaseStart | short_date_format }} - {{ car.leaseEnd | short_date_format }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Contract Term</td>
        <td>
          {{ car.contractTerm }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Monthly Payment</td>
        <td>
          {{ car.monthlyPayment | to_dollar_amount }}
        </td>
      </tr>
    </accordian-display>
    <accordian-display
      :icon="'fa-file-alt'"
      :title="'Insurance Details'">
      <tr>
        <td class="font-weight-bold">Payment</td>
        <td>
          {{ car.insurancePayment | to_dollar_amount }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Company</td>
        <td>
          {{ car.insuranceCompany }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Address</td>
        <td>
          {{ car.insuranceCompanyAddress }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Phone</td>
        <td>
          <a :href="`tel:${car.insuranceCompanyPhone}`">
            {{ car.insuranceCompanyPhone }}
          </a>
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Email</td>
        <td>
          {{ car.insuranceCompanyEmail }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Cancelled Date</td>
        <td>
          {{ car.insuranceCancelledDate | short_date_format }}
        </td>
      </tr>
    </accordian-display>
    <accordian-display
      :icon="'fa-handshake-o'"
      v-if="car.carSold"
      :title="'Car Sale Details'">
      <tr>
        <td class="font-weight-bold">Sold Date</td>
        <td>
          {{ car.soldDate | short_date_format }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Details</td>
        <td>
          {{ car.soldDetails }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Sold Price</td>
        <td>
          {{ car.soldPrice | to_dollar_amount }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Sold KM Reading</td>
        <td>
          {{ car.soldKmReading }}
        </td>
      </tr>
    </accordian-display>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import { ADD_PETROL_CARD, CANCEL_PETROL_CARD } from '@/vuex/types'
import AccordianDisplay from '@/components/displays/AccordianDisplay'
export default {
  name: 'CarDisplay',
  props: {
    car: {
      type: Object
    },
    lot: {
      type: Object
    }
  },
  components: {
    AccordianDisplay
  },
  computed: {
    lotLocation () {
      return 'https://maps.googleapis.com/maps/api/staticmap?center=' +
      this.lotInformation.latitude + ',' + this.lotInformation.longitude + '&zoom=18&size=500x500' +
      '&maptype=roadmap&markers=color:red%7Clabel:C%7C' +
      this.lotInformation.latitude + ',' + this.lotInformation.longitude +
      '&key=AIzaSyChsNbsujgDDgecCVkJNUkG2WFntMXPZ4c'
    },
    lotInformation () {
      // default lot
      let lot = this.car.Lot
      if (this.lot) lot = this.lot
      return lot
    },
    valets () {
      if (this.car.Valets) {
        let alreadyExists = []
        let results = []
        this.car.Valets.forEach(el => {
          if (!alreadyExists.includes(el.Account.uuid)) {
            alreadyExists.push(el.Account.uuid)
            results.push({
              ...el,
              count: 1
            })
          } else {
            let result = results.find(result => el.Account.uuid === result.Account.uuid)
            result.count++
          }
        })
        return results
      } else {
        return []
      }
    }
  },
  methods: {
    async executeAction (action, payload) {
      await this[action](payload)
    },
    newPetrolCard (car) {
      this.executeAction(ADD_PETROL_CARD, car)
    },
    cancelCard (card) {
      this.executeAction(CANCEL_PETROL_CARD, card)
    },
    ...mapActions([
      ADD_PETROL_CARD,
      CANCEL_PETROL_CARD
    ])
  }
}
</script>



// WEBPACK FOOTER //
// src/components/displays/CarDisplay.vue